import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Star, Send } from 'lucide-react';
import { isPromoActive, PLAN_PRICING, PROMO_DISCOUNT_LABEL } from '../lib/promo';

const PLANS = [
  {
    id: 'essentielle', name: 'Essentielle', sub: '1 cours / semaine', detail: '4 cours / mois',
    price: PLAN_PRICING.essentielle.regular, launchPrice: PLAN_PRICING.essentielle.launch, engagement: '12 mois', recommended: false,
    features: ['4 séances par mois', 'Accès à tous les créneaux', 'Réservation flexible'],
    emotion: 'Idéal pour débuter et installer une routine bien-être.'
  },
  {
    id: 'evolution', name: 'Évolution', sub: '2 cours / semaine', detail: '8 cours / mois',
    price: PLAN_PRICING.evolution.regular, launchPrice: PLAN_PRICING.evolution.launch, engagement: '12 mois', recommended: true,
    features: ['8 séances par mois', 'Progression optimale', 'Priorité de réservation'],
    emotion: 'Le choix le plus équilibré pour progresser efficacement.'
  },
  {
    id: 'transformation', name: 'Transformation', sub: '3 cours / semaine', detail: '12 cours / mois',
    price: PLAN_PRICING.transformation.regular, launchPrice: PLAN_PRICING.transformation.launch, engagement: '12 mois', recommended: false,
    features: ['12 séances par mois', 'Transformation accélérée', 'Suivi personnalisé'],
    emotion: 'Pour des résultats visibles et une transformation accélérée.'
  }
];

export default function NosAbonnements() {
  const promoActive = isPromoActive();
  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      {/* Hero */}
      <div className="gradient-cream py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99A55] mb-3 animate-fade-up">Nos formules — Pré-lancement</p>
          <h1 className="font-heading font-extralight text-4xl sm:text-5xl text-[#493329] tracking-tight mb-4 animate-fade-up delay-1">
            Choisissez votre rythme
          </h1>
          <div className="divider-premium" />
          <p className="text-[#7A6455] max-w-lg mx-auto animate-fade-up delay-2">
            Choisissez le rythme qui correspond à vos objectifs.
          </p>
          <p className="text-xs text-[#AE9885] italic max-w-lg mx-auto mt-3 animate-fade-up delay-3" data-testid="quality-note">
            Le nombre de places est volontairement limité afin de garantir une qualité d'accompagnement optimale.
          </p>
        </div>
      </div>

      {/* Plans */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((plan, i) => (
            <Card
              key={plan.id}
              className={`relative rounded-3xl overflow-hidden card-premium animate-fade-up ${
                plan.recommended
                  ? 'border-2 border-[#C99A55] bg-white shadow-lg shadow-[#C99A55]/8'
                  : 'border border-[#E9DDCC] bg-white'
              }`}
              style={{ animationDelay: `${0.1 + i * 0.1}s` }}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.recommended && (
                <div className="bg-[#C99A55] text-white text-xs text-center py-2 uppercase tracking-widest flex items-center justify-center gap-1.5 font-medium">
                  <Star className="w-3 h-3" fill="currentColor" /> Le plus populaire
                </div>
              )}
              <CardContent className="p-7">
                <div className="mb-6">
                  <h3 className="font-heading font-light text-2xl text-[#493329] mb-1">{plan.name}</h3>
                  <p className="text-sm text-[#C99A55]">{plan.sub}</p>
                  <p className="text-xs text-[#7A6455] mt-0.5">{plan.detail}</p>
                </div>

                <div className="mb-6">
                  {promoActive ? (
                    <>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-lg font-heading text-[#AE9885] line-through decoration-[#C99A55]/40" data-testid={`plan-old-price-${plan.id}`}>
                          {plan.price}€
                        </span>
                        <span className="text-4xl font-heading font-extralight text-[#493329]" data-testid={`plan-launch-price-${plan.id}`}>
                          {plan.launchPrice}
                        </span>
                        <span className="text-[#7A6455]">€ / mois</span>
                      </div>
                      <p className="text-[11px] uppercase tracking-widest text-[#C99A55] font-medium mt-1.5" data-testid={`plan-discount-label-${plan.id}`}>
                        {PROMO_DISCOUNT_LABEL}
                      </p>
                    </>
                  ) : (
                    <div className="flex items-baseline" data-testid={`plan-regular-price-${plan.id}`}>
                      <span className="text-4xl font-heading font-extralight text-[#493329]">{plan.price}</span>
                      <span className="text-[#7A6455] ml-1">€ / mois</span>
                    </div>
                  )}
                  <p className="text-xs text-[#7A6455] mt-2">Engagement {plan.engagement}</p>
                </div>

                <div className="space-y-2.5 mb-6">
                  {plan.features.map((f, j) => (
                    <div key={j} className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded-full bg-[#8C9B86]/15 flex items-center justify-center flex-shrink-0">
                        <Check className="w-2.5 h-2.5 text-[#8C9B86]" strokeWidth={2.5} />
                      </div>
                      <span className="text-sm text-[#493329]">{f}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-[#7A6455] italic mb-6 leading-relaxed">{plan.emotion}</p>

                <Link to={`/register?plan=${plan.id}`}>
                  <Button
                    className={`w-full rounded-xl h-12 btn-premium ${
                      plan.recommended
                        ? 'bg-[#C99A55] hover:bg-[#B08A4F] text-white'
                        : 'bg-[#493329] hover:bg-[#5A4238] text-white'
                    }`}
                    data-testid={`plan-btn-${plan.id}`}
                  >
                    Choisir cette formule
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Options complémentaires */}
        <div className="mt-16">
          <h2 className="font-heading font-extralight text-2xl text-[#493329] text-center mb-8">
            Options complémentaires
          </h2>
          <div className="grid grid-cols-1 max-w-md mx-auto">
            {/* Cours à l'unité */}
            <Card className="bg-white border-[#E9DDCC] rounded-3xl card-premium animate-fade-up delay-4" data-testid="single-class-card">
              <CardContent className="p-7">
                <h3 className="font-heading font-light text-xl text-[#493329] mb-1">Cours à l'unité</h3>
                <div className="flex items-baseline mb-3">
                  <span className="text-3xl font-heading font-extralight text-[#493329]">35</span>
                  <span className="text-[#7A6455] ml-1">€</span>
                </div>
                <p className="text-xs text-[#7A6455] mb-1">Sans engagement</p>
                <p className="text-sm text-[#7A6455] mb-5 leading-relaxed">Idéal pour découvrir le studio ou compléter votre pratique.</p>
                <Link to="/register?plan=single">
                  <Button variant="outline" className="w-full border-[#C99A55] text-[#C99A55] rounded-xl h-11 hover:bg-[#F0E8D9]" data-testid="single-class-btn">
                    Accéder au pré-lancement
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Expérience privée — invitation discrète, pas de tarif visible ici */}
        <div className="mt-14 max-w-xl mx-auto text-center" data-testid="private-experience-invite">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C99A55] font-semibold mb-2">Sur invitation</p>
          <h3 className="font-heading font-light text-2xl text-[#493329] mb-3">Expérience privée PURO</h3>
          <p className="text-sm text-[#7A6455] leading-relaxed max-w-md mx-auto mb-5">
            Un accompagnement privé et personnalisé, dans un cadre intimiste et exclusif. Réservé aux demandes étudiées au cas par cas.
          </p>
          <Link to="/cours-prive" className="text-sm text-[#9A744C] underline underline-offset-4 decoration-[#C99A55]/40 hover:text-[#C99A55] transition-colors" data-testid="private-experience-link">
            Faire une demande privée
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-16 text-center">
          <p className="text-sm text-[#7A6455]">
            Une question sur nos formules ?{' '}
            <Link to="/contact" className="text-[#C99A55] underline underline-offset-4">Contactez-nous</Link>
          </p>
          <p className="mt-4 text-xs text-[#AE9885] italic max-w-xl mx-auto leading-relaxed" data-testid="prelaunch-protection-note">
            Les conditions de l'offre de lancement sont maintenues pour toutes les personnes déjà préinscrites.
          </p>
        </div>
      </div>
    </div>
  );
}
